/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/ViewB2BRegistrationContent/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import styles from "./ViewB2BRegistrationContent.module.css";

import { ReactComponent as CalendarIcon } from "../../assets/icons/ViewRegistrationModal/CalendarIcon.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/ViewRegistrationModal/ClockIcon.svg";
import { ReactComponent as UserNameIcon } from "../../assets/icons/UserViews/UserNameIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";
import { CircularProgress } from "@mui/material";

/**
 * Shared body for B2B registration details (date, times, QR, virtual queue).
 * Used by {@link ViewB2BRegistrationModal} and {@link ViewB2BRegistrationView}.
 *
 * @param {Object} props
 * @param {Object} props.event - B2B event document (`_id`, `nombre`).
 * @param {Object} props.appointment - Appointment (`_id`, `fecha`, `horaInicio`, `horaFin`, `posicionEnFilaVirtual`, `nombreUsuarioComprador`, `nombreComercial`).
 * @param {"modal"|"page"} [props.variant="modal"] - `modal` keeps title inset for a top-right close control; `page` does not.
 * @param {function} [props.onBack] - With `variant="page"` (mobile), renders a go-back control above the title unless `hideMobileBackRow`.
 * @param {boolean} [props.hideMobileBackRow=false] - When true with `variant="page"`, do not render the inline back row (e.g. parent supplies it).
 */
const ViewB2BRegistrationContent = ({
  event,
  appointment,
  variant = "modal",
  onBack,
  hideMobileBackRow = false,
}) => {
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [isQrLoading, setIsQrLoading] = useState(false);

  useEffect(() => {
    if (event && appointment?._id) {
      setIsQrLoading(true);
      const userId = localStorage.getItem("userId");
      const qrData = {
        userId,
        idEventoB2B: event._id,
        idCita: appointment._id,
      };
      const dataString = JSON.stringify(qrData);
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(dataString)}`;
      setQrCodeUrl(qrUrl);
    } else {
      setQrCodeUrl(null);
      setIsQrLoading(false);
    }
  }, [event, appointment]);

  const titleClass =
    variant === "page"
      ? `${styles.header} ${styles.headerPage}`
      : styles.header;

  const showMobileBack =
    variant === "page" && typeof onBack === "function" && !hideMobileBackRow;

  return (
    <>
      {showMobileBack && (
        <div className={styles.mobileBackRow}>
          <button
            type="button"
            className={styles.mobileBackButton}
            onClick={onBack}
          >
            <ChevronLeftIcon className={styles.mobileBackIcon} aria-hidden />
            <span>{t("common.goBack")}</span>
          </button>
        </div>
      )}

      <div className={titleClass}>
        <h2>{event?.nombre}</h2>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.infoBox}>
          <div className={styles.infoHeader}>
            <CalendarIcon className={styles.icon} />
            <span>{t("components.viewRegistrationModal.buyerCompany")}</span>
          </div>
          <div className={styles.infoValue}>
            {appointment?.nombreComercial || "-"}
          </div>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoHeader}>
            <CalendarIcon className={styles.icon} />
            <span>{t("components.viewRegistrationModal.eventDate")}</span>
          </div>
          <div className={styles.infoValue}>
            {appointment?.fecha ? dayjs(appointment.fecha).format("LL") : "-"}
          </div>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoHeader}>
            <UserNameIcon className={styles.icon} />
            <span>{t("components.viewRegistrationModal.buyerExecutive")}</span>
          </div>
          <div className={styles.infoValue}>
            {appointment?.nombreUsuarioComprador || "-"}
          </div>
        </div>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.infoBox}>
          <div className={styles.infoHeader}>
            <ClockIcon className={styles.icon} />
            <span>
              {t("components.viewRegistrationModal.appointmentStartTime")}
            </span>
          </div>
          <div className={styles.infoValue}>
            {appointment?.horaInicio ?? "-"}
          </div>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoHeader}>
            <ClockIcon className={styles.icon} />
            <span>
              {t("components.viewRegistrationModal.appointmentEndTime")}
            </span>
          </div>
          <div className={styles.infoValue}>{appointment?.horaFin ?? "-"}</div>
        </div>
      </div>

      <div className={styles.qrContainer}>
        {qrCodeUrl && (
          <div
            className={styles.qrSlot}
            aria-busy={isQrLoading}
            aria-label={t("components.viewRegistrationModal.qrCodeLoading")}
          >
            {isQrLoading && (
              <div className={styles.qrSpinner}>
                <CircularProgress
                  size={56}
                  thickness={4}
                  sx={{ color: "#4eabe7" }}
                />
              </div>
            )}
            <img
              src={qrCodeUrl}
              alt=""
              className={`${styles.qrCode} ${isQrLoading ? styles.qrCodeLoading : ""}`}
              onLoad={() => setIsQrLoading(false)}
              onError={() => setIsQrLoading(false)}
            />
          </div>
        )}
        <div className={styles.virtualQueueBlock}>
          <span className={styles.virtualQueueLabel}>
            {t("components.viewRegistrationModal.virtualQueuePosition")}
          </span>
          <span className={styles.virtualQueueValue}>
            {appointment?.posicionEnFilaVirtual != null
              ? appointment.posicionEnFilaVirtual
              : "-"}
          </span>
        </div>
      </div>
    </>
  );
};

export default ViewB2BRegistrationContent;
